import React, { useCallback } from 'react'
import {
  ACTOR_TYPE,
  Document,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { LINKER_MODEL_IDS } from 'src/lib/Linkers/Linkers.constants'
import { useHasLinker } from 'src/pages/Receptors/receptors.hooks'
import { BrokenLinkIcon } from 'src/components/Icons/Basic/BrokenLinkIcon'
import { MIME_TYPE } from 'src/Utils/globalEnums'
import { Content } from 'src/context/ContentContext'
import {
  CsvIcon,
  ExcelIcon,
  GoogleDocsIcon,
  GoogleDriveIcon,
  GoogleSheetsIcon,
  GoogleSlidesIcon,
  ImageIcon,
  KeynoteIcon,
  LinkIcon,
  NumbersIcon,
  PagesIcon,
  PdfIcon,
  PowerpointIcon,
  SharePointIcon,
  TextIcon,
  WordIcon,
  UnknownSaaSIcon,
} from '../../Icons/FileType'
import { ReactComponent as ZipIcon } from '../../Icons/assets/file-upload-icon.svg'
import { ReactComponent as ArtifactIcon } from '../../Icons/Basic/icons_artifact_data.svg'
import { ContentStaticHelper } from '../../../context/Content/statichelpers'
import { useContent } from '../../../context/hooks'

export type SVGComponent = React.FunctionComponent<
  React.SVGProps<SVGSVGElement> & {
    title?: string | undefined
  }
>

export const useFileTypeIcon = ({
  document,
  mime,
}: {
  document?: Document.AsObject
  mime?: string
}): SVGComponent => {
  const content = useContent()
  const { data } = useHasLinker(
    document?.actor as LINKER_MODEL_IDS,
    !!document && document.actortype === ACTOR_TYPE.LINKER,
  )

  if (document === undefined && mime === undefined) {
    throw Error("Must pass the document, or the mime to 'useFileTypeIcon'")
  }

  if (document && document.actortype === ACTOR_TYPE.RECEPTOR) {
    return ContentStaticHelper.getObjById(content, document.actor).logo
  }
  if (document && document.actortype === ACTOR_TYPE.LINKER) {
    return getLinkerIcon(
      document.actor as LINKER_MODEL_IDS,
      data?.getHasLinker() || false,
    )
  }
  return getMimeTypeIcon((document?.mime || mime) as string)
}

export type EvidenceGroupFileTypeIcon = {
  isAutomated: boolean
  isLinkedDocument: boolean
  mime: string
  serviceModelId?: string
  content: Content
}

export const getEvidenceGroupFileTypeIcon = ({
  isAutomated,
  isLinkedDocument,
  mime,
  serviceModelId,
  content,
}: EvidenceGroupFileTypeIcon): SVGComponent => {
  let actualMime = mime

  // if multipart
  if (mime.startsWith('multipart/tr-mixed')) {
    actualMime = mime.split('; ')[1]
  }

  if (isAutomated || isLinkedDocument) {
    if (actualMime !== 'application/protobuf') {
      return getMimeTypeIcon(actualMime)
    }
    return serviceModelId && content
      ? ContentStaticHelper.getObjById(content, serviceModelId)?.logo ||
          UnknownSaaSIcon
      : actualMime
      ? getMimeTypeIcon(actualMime)
      : UnknownSaaSIcon
  }

  return getMimeTypeIcon(actualMime)
}

export const useFileTypeIconForEvidence = (
  isAutomated: boolean,
  mime: string,
  isLinkedDocument: boolean,
  serviceModelId?: string,
): SVGComponent => {
  const content = useContent()

  return getEvidenceGroupFileTypeIcon({
    isAutomated,
    isLinkedDocument,
    mime,
    serviceModelId,
    content,
  })
}

/**
 * Similar to useFileTypeIconForEvidence, but returns a function that takes a mime type and returns an SVG component.
 * This is useful for generating icons for a list of evidence groups.
 */
export const useFileTypeIconForEvidenceGroups = (): ((
  isAutomated: boolean,
  mime: string,
  isLinkedDocument: boolean,
  serviceModelId?: string,
) => SVGComponent) => {
  const content = useContent()

  return useCallback(
    (
      isAutomated: boolean,
      mime: string,
      isLinkedDocument: boolean,
      serviceModelId?: string,
    ): SVGComponent => {
      return getEvidenceGroupFileTypeIcon({
        isAutomated,
        isLinkedDocument,
        mime,
        serviceModelId,
        content,
      })
    },
    [content],
  )
}

export const useActorIconForEvidence = (
  isAutomated: boolean,
  mime: string,
  actor: string,
): SVGComponent => {
  const content = useContent()

  if (isAutomated) {
    return (
      ContentStaticHelper.getObjById(content, actor)?.logo || UnknownSaaSIcon
    )
  }

  return getMimeTypeIcon(mime)
}

export const getMimeTypeIcon = (mimeType: string): SVGComponent => {
  if (mimeType?.startsWith('tr-artifact/')) return ArtifactIcon
  if (isCsv(mimeType)) return CsvIcon
  if (isExcel(mimeType)) return ExcelIcon
  if (isImage(mimeType)) return ImageIcon
  if (isKeynote(mimeType)) return KeynoteIcon
  if (isLink(mimeType)) return LinkIcon
  if (isNumbers(mimeType)) return NumbersIcon
  if (isPages(mimeType)) return PagesIcon
  if (isPdf(mimeType)) return PdfIcon
  if (isPowerpoint(mimeType)) return PowerpointIcon
  if (isText(mimeType)) return TextIcon
  if (isWord(mimeType)) return WordIcon
  if (isGoogleDoc(mimeType)) return GoogleDocsIcon
  if (isGoogleSheets(mimeType)) return GoogleSheetsIcon
  if (isGoogleSlides(mimeType)) return GoogleSlidesIcon
  if (isZip(mimeType)) return ZipIcon
  return TextIcon
}

const isCsv = (mime: string): boolean => {
  // application/csv is the other mime browsers and mime-db report for a .csv;
  // both are accepted for upload (INDEXABLE_UPLOAD_MIMES).
  return mime === 'text/csv' || mime === 'application/csv'
}

const isExcel = (mime: string): boolean => {
  return (
    mime === 'application/vnd.ms-excel' ||
    mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  )
}

const isImage = (mime: string): boolean => {
  // Any image renders with the image icon. Listing png/jpeg alone left GIF and
  // WEBP -- both accepted for upload -- falling through to the generic text
  // icon, which is wrong wherever a mime icon is shown.
  return !!mime?.startsWith('image/')
}

const isKeynote = (mime: string): boolean => {
  return mime === MIME_TYPE.APPLE_KEYNOTE || mime === MIME_TYPE.APPLE_KEYNOTE_2
}

export const isLink = (mime: string): boolean => {
  return mime === 'text/uri-list'
}

const isNumbers = (mime: string): boolean => {
  return mime === MIME_TYPE.APPLE_NUMBERS || mime === MIME_TYPE.APPLE_NUMBERS_2
}

const isPages = (mime: string): boolean => {
  return mime === MIME_TYPE.APPLE_PAGES || mime === MIME_TYPE.APPLE_PAGES_2
}

const isPdf = (mime: string): boolean => {
  return mime === 'application/pdf'
}

const isPowerpoint = (mime: string): boolean => {
  return (
    mime === 'application/vnd.ms-powerpoint' ||
    mime ===
      'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  )
}

export const isMarkdown = (mime: string): boolean => {
  return mime === 'text/markdown'
}

const isText = (mime: string): boolean => {
  return (
    mime === 'text/rtf' ||
    // What browsers and mime-db report for .rtf; ntrced normalizes it to
    // text/rtf, so both reach the UI depending on where the value came from.
    mime === 'application/rtf' ||
    mime === 'text/plain' ||
    mime === 'application/json' ||
    isMarkdown(mime) ||
    mime === 'application/vnd.oasis.opendocument.text'
  )
}

const isWord = (mime: string): boolean => {
  return (
    mime === 'application/msword' ||
    mime ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  )
}

const isGoogleDoc = (mime: string): boolean => {
  return mime === 'application/vnd.google-apps.document'
}

const isGoogleSheets = (mime: string): boolean => {
  return mime === 'application/vnd.google-apps.spreadsheet'
}

const isGoogleSlides = (mime: string): boolean => {
  return mime === 'application/vnd.google-apps.presentation'
}

export const getLinkerIcon = (
  linkerId: LINKER_MODEL_IDS,
  isConnected: boolean,
): SVGComponent => {
  if (!isConnected) {
    return BrokenLinkIcon
  }
  switch (linkerId) {
    case LINKER_MODEL_IDS.GOOGLE_DRIVE:
      return GoogleDriveIcon
    case LINKER_MODEL_IDS.SHAREPOINT:
      return SharePointIcon
    default:
      return TextIcon
  }
}

const isZip = (mime: string): boolean => {
  return mime === 'application/zip' || mime === 'application/tr-archive'
}
